import { ChartStyle } from "@/components/charts/ChartWrapper";
import {
  MetricChartPlotStyle,
  MetricChartPlotStyleEnum,
} from "@/components/charts/use-uplot-time-series-chart-options";
import { DataLineageViewKind } from "@/components/data-lineage/DataLineage.types";
import { DynamicQueryParam as QueryParam } from "@/constants/query-params";
import { ArgoWorkflowPhase } from "@/gen_src/proto/chalk/argo/v1/workflow_pb";
import {
  AlertSeverityKind,
  ChartLinkKind,
  GroupByKind,
} from "@/gen_src/proto/chalk/artifacts/v1/chart_pb";
import { ScriptTaskStatus } from "@/gen_src/proto/chalk/common/v1/script_task_pb";
import { OperationStatus } from "@/gen_src/proto/chalk/server/v1/batch_pb";
import {
  WorkflowExecutionStatus,
  WorkflowKind,
} from "@/gen_src/proto/chalk/server/v1/dataplaneworkflows_pb";
import {
  OfflineQueryKind,
  OfflineQueryStatus,
} from "@/gen_src/proto/chalk/server/v1/offline_queries_pb";
import { parseRawTimestamp } from "@/utils/date-time";
import { isDefined } from "@/utils/function-utils";
import { useStableArray } from "@/utils/hooks/use-memo-array";
import { stringToSafeBoolean } from "@/utils/string-utils";
import { ColumnSort } from "@tanstack/table-core";
import { DateRangePreset } from "@/components/interactive/SharedTimeRange.types";
// Importing nuqs, the only place we're allowed to
// eslint-disable-next-line no-restricted-imports
import {
  Parser,
  ParserBuilder,
  createParser,
  UseQueryStateReturn,
  UseQueryStatesReturn,
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  parseAsStringEnum,
  useQueryState as useNuqsQueryState,
  useQueryStates as useNuqsQueryStates,
  type Options as NuqsOptions,
} from "nuqs";
import { useCallback, useMemo } from "react";

export { QueryParam };

const isParserBuilder = <T>(parser: Parser<T>): parser is ParserBuilder<T> => {
  return "withDefault" in parser;
};

// The type we expect query params to be expressed in code
// To add a new Query Param:
//  - Add a field with the type expected to use in code
//  - Write a parser or serializer to transform to/from a string (follow QueryParam.START_DATE)
//  - or use one of the provided parsers (follow QueryParam.OFFLINE_QUERY_TYPE)
export interface QueryState extends Record<string, unknown> {
  /* Used Frequently */
  [QueryParam.QUERY_STRING]: string;
  [QueryParam.TABLE_ROW_HEIGHT]: string;
  [QueryParam.CLUSTER_ID]: string;
  [QueryParam.LOG_AGGREGATION]: string;
  // Raw string at the base layer: no shared parser validates this param, so a
  // narrower type here would lie to callers that skip a custom parse. The
  // test-page hook (useTestPageViewModeQueryParam) narrows it via its parser.
  [QueryParam.TEST_COMPONENTS_DATA_STATE]: string;
  [QueryParam.TEST_COMPONENTS_EXAMPLE]: string;
  [QueryParam.TEST_COMPONENTS_PANE]: string;
  [QueryParam.TEST_COMPONENTS_SAMPLE_PLAN]: string;
  [QueryParam.TEST_COMPONENTS_METADATA_ZIP]: string;
  [QueryParam.SEARCH_TYPE]: string;
  [QueryParam.ENVIRONMENT]: string;
  [QueryParam.SIMPLE_DATE_RANGE_PRESET]: string;
  // Dates are stored as milliseconds since epoch in the url parameters
  [QueryParam.START_DATE]: Date | null;
  [QueryParam.END_DATE]: Date | null;
  [QueryParam.LOOKBACK_MS]: number | null;
  /** Legacy preset id ("6h", "current-month"), read only to migrate old links onto ds/de/lb. */
  [QueryParam.DATE_RANGE_PRESET]: string;

  /* others, alphabetically sorted */
  [QueryParam.AGENT_ID]: string;
  [QueryParam.AUDIT_ENDPOINTS]: string[];
  [QueryParam.BRANCH]: string;
  [QueryParam.DEPLOYMENT_ID]: string;
  [QueryParam.DEPLOYMENT_IDS]: string[];
  [QueryParam.QUERY_NAME]: string;
  [QueryParam.QUERY_VERSION]: string;
  [QueryParam.META_QUERY_ID_FILTER]: string;
  [QueryParam.CORRELATION_ID]: string;
  [QueryParam.STATUS_FILTER]: string;
  [QueryParam.MCP_TRANSPORT_FILTER]: string;
  [QueryParam.MCP_CREDENTIAL_FILTER]: string;
  [QueryParam.TRACES_FILTER]: string;
  [QueryParam.STARTED_LOOKBACK]: string;
  [QueryParam.TRACE_ID]: string;
  [QueryParam.PAGE_VIEW]: string;
  [QueryParam.TRACE_MIN_DURATION_MS]: string;
  [QueryParam.TRACE_MAX_DURATION_MS]: string;
  [QueryParam.TRACE_MIN_SPAN_COUNT]: string;
  [QueryParam.TRACE_MAX_SPAN_COUNT]: string;
  [QueryParam.RESOURCE_GROUP]: string;
  [QueryParam.ROUTER_USAGE_API_KEY_FILTER]: string;
  [QueryParam.ROUTER_USAGE_API_KEY_LABEL_FILTER]: string;
  [QueryParam.ROUTER_USAGE_CONNECTION_FILTER]: string;
  [QueryParam.ROUTER_USAGE_END_USER_FILTER]: string;
  [QueryParam.ROUTER_USAGE_MODEL_FILTER]: string;
  [QueryParam.ROUTER_USAGE_MODEL_ROUTE_FILTER]: string;
  [QueryParam.ROUTER_USAGE_POOL_FILTER]: string;
  [QueryParam.CHART_ALERT_SEVERITY]: AlertSeverityKind;
  [QueryParam.CHART_GROUP_BYS]: GroupByKind[];
  [QueryParam.CHART_LINKED_ENTITY_KIND]: ChartLinkKind;
  [QueryParam.CHART_LINKED_ENTITY_ID]: string;
  [QueryParam.CHART_PLOT_STYLE]: MetricChartPlotStyle;
  [QueryParam.CHART_STYLE]: ChartStyle;
  [QueryParam.DASHBOARD_SHOW_INCIDENTS]: boolean | null;
  [QueryParam.DASHBOARD_SHOW_INCIDENT_RANGES]: boolean | null;
  [QueryParam.DASHBOARD_SHOW_DEPLOYMENTS]: boolean | null;
  [QueryParam.DASHBOARD_SHOW_KUBE_PATCHES]: boolean | null;
  [QueryParam.DASHBOARD_SHOW_CUSTOM_ANNOTATIONS]: boolean | null;
  // Annotation incident scope; absent until the user picks (mirrors the localStorage toggle state).
  [QueryParam.DASHBOARD_INCIDENT_SCOPE]: "all" | "related";
  [QueryParam.METADATA_JOB_STATUS_PROTO]: number;
  [QueryParam.METADATA_SCHEDULED_JOB_TAG]: string;
  [QueryParam.JOB_QUEUE_STATE_FILTER]: string;
  [QueryParam.JOB_QUEUE_KIND_FILTER]: string;
  [QueryParam.JOB_OPERATION_ID_FILTER]: string;
  [QueryParam.OFFLINE_WIDE_TABLE_TRIGGER_KIND_FILTER]: string;
  [QueryParam.OFFLINE_WIDE_TABLE_KIND_FILTER]: string;
  [QueryParam.RERUN_OPERATION_ID]: string;
  [QueryParam.RERUN_TIMESTAMP]: Date | null;
  [QueryParam.DATA_LINEAGE_DATASOURCE]: string;
  [QueryParam.DATA_LINEAGE_TABLE]: string;
  [QueryParam.DATA_LINEAGE_FEATURE]: string;
  [QueryParam.DATA_LINEAGE_RESOLVER]: string;
  [QueryParam.DATA_LINEAGE_COLUMN]: string;
  [QueryParam.DATA_LINEAGE_VIEW]: DataLineageViewKind;
  [QueryParam.DOCKER_IMAGES]: string[];
  [QueryParam.ERROR_STATUS]: boolean | null;
  [QueryParam.EVALUATION_COMPARE]: string;
  [QueryParam.EVALUATION_POST_TRAINING_ID]: string;
  [QueryParam.EVALUATION_RUN_ID]: string;
  [QueryParam.EVALUATION_RUN_IDS]: string[];
  [QueryParam.EVALUATION_CHART_TAB]: string;
  [QueryParam.EVALUATION_CHART_DISPLAY]: string;
  [QueryParam.EVALUATION_SERIES]: string[];
  [QueryParam.EVALUATION_RUN_VIEW]: string;
  [QueryParam.EVALUATION_SUITE_ID]: string;
  [QueryParam.FEATURE_FQN]: string;
  [QueryParam.FEATURE_TAGS]: string[];
  [QueryParam.FEATURE_FILTERS]: string[];
  [QueryParam.FEATURE_TYPES]: string[];
  [QueryParam.FILTER]: string;
  [QueryParam.HAS_TRACE]: boolean;
  [QueryParam.KUBERNETES_GROUP_VIEW]: string;
  [QueryParam.KUBERNETES_INCLUDE_NON_ENVIRONMENT_PODS]: boolean | null;
  [QueryParam.KUBERNETES_SHOW_DAEMONSET_PODS]: boolean | null;
  [QueryParam.KUBERNETES_ONLY_CURRENT_ENV_PODS]: boolean | null;
  [QueryParam.KUBERNETES_NODE_FILTER]: string;
  [QueryParam.KUBERNETES_POD_FILTER]: string;
  [QueryParam.KUBERNETES_NODE_SORT]: string;
  [QueryParam.KUBERNETES_ROUTE_FILTER]: string;
  [QueryParam.TEAM_MEMBERS_SHOW_DEACTIVATED]: boolean | null;
  [QueryParam.LOG_SEVERITIES]: string[];
  [QueryParam.SUBSCRIPTIONS]: string[];
  [QueryParam.LOG_ORDER]: "newest-first" | "oldest-first";
  [QueryParam.LOG_ID]: string;
  [QueryParam.LOG_HIGHLIGHT_ID]: string;
  [QueryParam.LOG_COLUMNS]: string[];
  [QueryParam.ACCESS_LOG_COLUMNS]: string[];
  [QueryParam.TILES_GROUP_BYS]: string[];
  [QueryParam.TILES_SELECTED_KEY]: string;
  [QueryParam.TILES_SHOW_GAPS]: boolean | null;
  [QueryParam.USAGE_GROUP_BYS]: string[];
  [QueryParam.USAGE_DAYS_BACK]: number | null;
  [QueryParam.USAGE_MONTH_INDEX]: number | null;
  [QueryParam.KUBE_EVENT_ID]: string;
  [QueryParam.MESSAGE]: string;
  [QueryParam.NOTEBOOK_CELL_ID]: string;
  [QueryParam.NOTEBOOK_PANEL]: string;
  [QueryParam.NOTEBOOK_VIEW]: string;
  [QueryParam.DASHBOARD_VIEW]: string;
  [QueryParam.LATENCY_MS]: string;
  [QueryParam.NAMED_QUERY]: string;
  [QueryParam.NAMED_QUERIES]: string[];
  [QueryParam.OUTPUT_OF_NAMED_QUERIES]: string[];
  [QueryParam.NAMESPACE]: string;
  [QueryParam.NAMESPACES]: string[];
  [QueryParam.OWNER_EMAIL]: string;
  [QueryParam.OWNER_EMAILS]: string[];
  [QueryParam.OFFLINE_QUERY_STATUS_PROTO]: OfflineQueryStatus;
  [QueryParam.OFFLINE_QUERY_TYPE_PROTO]: OfflineQueryKind;
  [QueryParam.OFFLINE_QUERY_STATUSES]: string;
  [QueryParam.OFFLINE_QUERY_TYPES]: string;
  [QueryParam.OFFLINE_QUERY_UTILIZATION_LIMIT]: number;
  [QueryParam.SCRIPT_TASK_STATUS_PROTO]: ScriptTaskStatus;
  [QueryParam.ARGO_BUILD_PHASE_PROTO]: ArgoWorkflowPhase;
  [QueryParam.SQL_QUERY_STATUS]: number;
  [QueryParam.DF_RUN_STATUS]: number;
  [QueryParam.OPERATION_STATUS]: OperationStatus;
  [QueryParam.OPERATOR]: string;
  [QueryParam.PLAN_DETAILS_SELECTION]: string;
  [QueryParam.PLAN_VIEW_SETTINGS]: string;
  [QueryParam.OPERATOR_TYPE]: string[];
  [QueryParam.PAGE]: number;
  [QueryParam.NAMED_QUERIES_PAGE_TOKEN]: string;
  [QueryParam.PIPELINE_IDS]: number[];
  [QueryParam.RESOLVER_CONTEXT]: string[];
  [QueryParam.RESOLVER_FQN]: string;
  [QueryParam.RESOLVER_FQNS]: string;
  [QueryParam.SHARD_IDS]: number[];
  [QueryParam.PERF_SUMMARY_SHARD_ID]: number;
  [QueryParam.PERF_SUMMARY_VIEW]: string;
  [QueryParam.SORTED_COLUMNS]: ColumnSort[];
  [QueryParam.VIEW]: string;
  [QueryParam.VISIBLE_COLUMNS]: string[];
  [QueryParam.FROZEN_COLUMNS]: string[];
  [QueryParam.VOLUME_MOUNT]: string;
  [QueryParam.VOLUME_PREFIX]: string;
  [QueryParam.VOLUME_FILE]: string;
  [QueryParam.VOLUME_VERSION]: string;
  [QueryParam.VOLUME_REF]: string;
  [QueryParam.COMPUTE_STATUS]: string[];
  [QueryParam.COMPUTE_TAGS]: string[];
  [QueryParam.COMPUTE_MODEL_NAME]: string[];
  [QueryParam.TILES_QUERY]: string;
  [QueryParam.NAMED_QUERY_VERSION]: string;
  [QueryParam.WORKSHEET_ID]: string;
}

// Our own parsing, to create more complicated objects that nuqs does not support natively
// (ex. Dates)
const getCustomParser =
  <T extends QueryParam>(param: T) =>
  (valueStr: string | null): QueryState[T] | null => {
    switch (param) {
      case QueryParam.START_DATE:
      case QueryParam.END_DATE: {
        try {
          const decodedString =
            typeof valueStr === "string" ? decodeURIComponent(valueStr) : undefined;
          if (!decodedString) {
            return null;
          }

          return parseRawTimestamp(decodedString) as QueryState[T];
        } catch {
          return null;
        }
      }
      case QueryParam.HAS_TRACE:
      case QueryParam.ERROR_STATUS:
      case QueryParam.KUBERNETES_INCLUDE_NON_ENVIRONMENT_PODS:
      case QueryParam.KUBERNETES_SHOW_DAEMONSET_PODS:
      case QueryParam.KUBERNETES_ONLY_CURRENT_ENV_PODS:
      case QueryParam.TEAM_MEMBERS_SHOW_DEACTIVATED:
      case QueryParam.DASHBOARD_SHOW_INCIDENTS:
      case QueryParam.DASHBOARD_SHOW_INCIDENT_RANGES:
      case QueryParam.DASHBOARD_SHOW_DEPLOYMENTS:
      case QueryParam.DASHBOARD_SHOW_KUBE_PATCHES:
      case QueryParam.DASHBOARD_SHOW_CUSTOM_ANNOTATIONS:
      case QueryParam.TILES_SHOW_GAPS:
        return stringToSafeBoolean(valueStr) as QueryState[T];
      default:
        // Note(kelvin): Hacky but lets us have nice types at the interface.
        // TODO better internal types
        return (valueStr || undefined) as unknown as QueryState[T];
    }
  };

// Our own serializer of objects into ones that nuqs understands
// Nuqs handles most things well, this helps interact with Dates, etc.
const getCustomSerializer =
  <T extends QueryParam>(param: T) =>
  (value: QueryState[T] | null | undefined): string => {
    switch (param) {
      case QueryParam.START_DATE:
      case QueryParam.END_DATE: {
        return value instanceof Date ? encodeURIComponent(value.getTime()) : "";
      }
      case QueryParam.ERROR_STATUS:
      case QueryParam.KUBERNETES_INCLUDE_NON_ENVIRONMENT_PODS:
      case QueryParam.KUBERNETES_SHOW_DAEMONSET_PODS:
      case QueryParam.KUBERNETES_ONLY_CURRENT_ENV_PODS:
      case QueryParam.TEAM_MEMBERS_SHOW_DEACTIVATED:
      case QueryParam.HAS_TRACE:
      case QueryParam.DASHBOARD_SHOW_INCIDENTS:
      case QueryParam.DASHBOARD_SHOW_INCIDENT_RANGES:
      case QueryParam.DASHBOARD_SHOW_DEPLOYMENTS:
      case QueryParam.DASHBOARD_SHOW_KUBE_PATCHES:
      case QueryParam.DASHBOARD_SHOW_CUSTOM_ANNOTATIONS:
      case QueryParam.TILES_SHOW_GAPS:
        return typeof value !== "boolean" ? "" : value ? `t` : `f`;
      default:
        return value == null ? "" : String(value);
    }
  };

// nuqs does its own parsing of the params, and comes built in with enum checking, etc.
const getParserForParam = <T extends QueryParam>(param: T): Parser<QueryState[T]> => {
  switch (param) {
    // We have custom parsers for these
    case QueryParam.ERROR_STATUS:
    case QueryParam.HAS_TRACE:
    case QueryParam.KUBERNETES_INCLUDE_NON_ENVIRONMENT_PODS:
    case QueryParam.KUBERNETES_SHOW_DAEMONSET_PODS:
    case QueryParam.KUBERNETES_ONLY_CURRENT_ENV_PODS:
    case QueryParam.START_DATE:
    case QueryParam.END_DATE:
    case QueryParam.TEAM_MEMBERS_SHOW_DEACTIVATED:
      return {
        parse: getCustomParser(param),
        serialize: getCustomSerializer(param),
      };
    // Annotation toggles: "replace" history so flipping them doesn't pile up back-button entries.
    case QueryParam.DASHBOARD_SHOW_INCIDENTS:
    case QueryParam.DASHBOARD_SHOW_INCIDENT_RANGES:
    case QueryParam.DASHBOARD_SHOW_DEPLOYMENTS:
    case QueryParam.DASHBOARD_SHOW_KUBE_PATCHES:
    case QueryParam.DASHBOARD_SHOW_CUSTOM_ANNOTATIONS:
    case QueryParam.TILES_SHOW_GAPS:
      return createParser({
        parse: getCustomParser(param),
        serialize: getCustomSerializer(param),
      }).withOptions({ history: "replace" }) as Parser<QueryState[T]>;
    case QueryParam.DASHBOARD_INCIDENT_SCOPE:
      return parseAsStringEnum(["all", "related"]).withOptions({
        history: "replace",
      }) as Parser<QueryState[T]>;
    // Nuqs provided parsers
    // String Arrays
    case QueryParam.EVALUATION_RUN_IDS:
    case QueryParam.EVALUATION_SERIES:
    case QueryParam.AUDIT_ENDPOINTS:
    case QueryParam.FEATURE_FILTERS:
    case QueryParam.FEATURE_TAGS:
    case QueryParam.FEATURE_TYPES:
    case QueryParam.FROZEN_COLUMNS:
    case QueryParam.SUBSCRIPTIONS:
    case QueryParam.NAMESPACES:
    case QueryParam.NAMED_QUERIES:
    case QueryParam.OUTPUT_OF_NAMED_QUERIES:
    case QueryParam.LOG_SEVERITIES:
    case QueryParam.LOG_COLUMNS:
    case QueryParam.ACCESS_LOG_COLUMNS:
    case QueryParam.TILES_GROUP_BYS:
    case QueryParam.USAGE_GROUP_BYS:
    case QueryParam.OPERATOR_TYPE:
    case QueryParam.BATCH_OP_TYPE:
    case QueryParam.VELOX_OP_TYPE:
    case QueryParam.STATIC_OP_TYPE:
    case QueryParam.OWNER_EMAILS:
    case QueryParam.RESOLVER_CONTEXT:
    case QueryParam.RESOLVER_FQNS:
    case QueryParam.COMPUTE_STATUS:
    case QueryParam.COMPUTE_TAGS:
    case QueryParam.COMPUTE_MODEL_NAME:
    case QueryParam.DOCKER_IMAGES:
    case QueryParam.DEPLOYMENT_IDS:
      return parseAsArrayOf(parseAsString).withOptions({ history: "push" }) as Parser<
        QueryState[T]
      >;
    // Numeric Arrays
    case QueryParam.SHARD_IDS:
    case QueryParam.PIPELINE_IDS:
    case QueryParam.CHART_GROUP_BYS: // note: proto enums are ints underlying. this is slightly incorrect.
      return parseAsArrayOf(parseAsInteger).withOptions({ history: "push" }) as Parser<
        QueryState[T]
      >;
    // enums
    case QueryParam.CHART_ALERT_SEVERITY:
    case QueryParam.CHART_LINKED_ENTITY_KIND:
      return parseAsInteger.withOptions({ history: "push" }) as Parser<QueryState[T]>;
    case QueryParam.CHART_PLOT_STYLE:
      return parseAsStringEnum(Object.values(MetricChartPlotStyleEnum)).withOptions({
        history: "push",
      }) as Parser<QueryState[T]>;
    case QueryParam.CHART_STYLE:
      return parseAsStringEnum(Object.values(ChartStyle)).withOptions({
        history: "push",
      }) as Parser<QueryState[T]>;
    case QueryParam.DATA_LINEAGE_VIEW:
      return parseAsStringEnum(Object.values(DataLineageViewKind)).withOptions({
        history: "push",
      }) as Parser<QueryState[T]>;
    case QueryParam.LOG_ORDER:
      return parseAsStringEnum(["newest-first", "oldest-first"]).withOptions({
        history: "push",
      }) as Parser<QueryState[T]>;
    case QueryParam.OFFLINE_QUERY_STATUS_PROTO:
      return parseAsInteger.withOptions({ history: "push" }) as Parser<QueryState[T]>;
    case QueryParam.OFFLINE_QUERY_TYPE_PROTO:
      return parseAsInteger.withOptions({ history: "push" }) as Parser<QueryState[T]>;
    case QueryParam.OFFLINE_QUERY_UTILIZATION_LIMIT:
      return parseAsInteger.withOptions({ history: "push" }) as Parser<QueryState[T]>;
    case QueryParam.SCRIPT_TASK_STATUS_PROTO:
      return parseAsInteger.withOptions({ history: "push" }) as Parser<QueryState[T]>;
    case QueryParam.ARGO_BUILD_PHASE_PROTO:
      return parseAsInteger.withOptions({ history: "push" }) as Parser<QueryState[T]>;
    case QueryParam.SQL_QUERY_STATUS:
      return parseAsInteger.withOptions({ history: "push" }) as Parser<QueryState[T]>;
    case QueryParam.DF_RUN_STATUS:
      return parseAsInteger.withOptions({ history: "push" }) as Parser<QueryState[T]>;
    case QueryParam.METADATA_JOB_STATUS_PROTO:
      return parseAsInteger.withOptions({ history: "push" }) as Parser<QueryState[T]>;
    case QueryParam.OPERATION_STATUS:
      return parseAsInteger.withOptions({ history: "push" }) as Parser<QueryState[T]>;
    case QueryParam.PERF_SUMMARY_SHARD_ID:
      return parseAsInteger.withOptions({ history: "push" }) as Parser<QueryState[T]>;
    case QueryParam.LOOKBACK_MS:
    case QueryParam.USAGE_DAYS_BACK:
    case QueryParam.USAGE_MONTH_INDEX:
      return parseAsInteger.withOptions({ history: "push" }) as Parser<QueryState[T]>;
    case QueryParam.PAGE:
      return parseAsInteger as Parser<QueryState[T]>;
    case QueryParam.VISIBLE_COLUMNS:
    case QueryParam.SORTED_COLUMNS:
      return parseAsString.withOptions({ history: "replace" }) as Parser<QueryState[T]>;
    // Notebook cell / tile row selection: replace so clicking through never
    // piles up back-button entries.
    case QueryParam.NOTEBOOK_CELL_ID:
    case QueryParam.TILES_SELECTED_KEY:
      return parseAsString.withOptions({ history: "replace" }) as Parser<QueryState[T]>;
    // Notebook side drawer pane: replace so toggling the drawer never piles up
    // back-button entries.
    case QueryParam.NOTEBOOK_PANEL:
      return parseAsString.withOptions({ history: "replace" }) as Parser<QueryState[T]>;
    // Notebook view-only mode: replace so toggling never piles up
    // back-button entries.
    case QueryParam.NOTEBOOK_VIEW:
      return parseAsString.withOptions({ history: "replace" }) as Parser<QueryState[T]>;
    // Dashboard view-only mode: replace so toggling never piles up
    // back-button entries.
    case QueryParam.DASHBOARD_VIEW:
      return parseAsString.withOptions({ history: "replace" }) as Parser<QueryState[T]>;
    case QueryParam.QUERY_STRING:
      // This should be a replace due to the potentially high number of pushes
      return parseAsString
        .withOptions({
          throttleMs: 250,
        })
        .withOptions({ history: "replace" }) as Parser<QueryState[T]>;
    case QueryParam.LOG_AGGREGATION:
      // Push so back/forward steps through aggregation-builder changes.
      return parseAsString.withOptions({ history: "push" }) as Parser<QueryState[T]>;
    // Plan viewer settings: replace so flipping a display toggle never piles up back-button
    // entries between the plans a reader actually navigated through.
    case QueryParam.PLAN_VIEW_SETTINGS:
      return parseAsString.withOptions({ history: "replace" }) as Parser<QueryState[T]>;
    case QueryParam.OPERATOR:
    case QueryParam.VOLUME_MOUNT:
    case QueryParam.VOLUME_PREFIX:
    case QueryParam.VOLUME_FILE:
    case QueryParam.VOLUME_VERSION:
    case QueryParam.VOLUME_REF:
    default:
      return parseAsString.withOptions({ history: "push" }) as Parser<QueryState[T]>;
  }
};

export type UseTypesafeQueryStateHook<
  T extends QueryParam,
  V extends QueryState[T] = QueryState[T],
  DV extends V | null = null,
> = UseQueryStateReturn<V, DV>;

export type UseTypesafeQueryStateHookNonNull<
  T extends QueryParam,
  V extends QueryState[T] = QueryState[T],
> = UseQueryStateReturn<V, "">;

export interface UseTypesafeQueryStateOptions<
  T extends QueryParam,
  V extends QueryState[T] = QueryState[T],
  DV extends V | null = null,
> extends Partial<Parser<QueryState[T]>>,
    NuqsOptions {
  clearOnDefault?: boolean;
  defaultValue?: NonNullable<DV>;
  disabled?: boolean;
  /** If your usage has a special serialization of the data, you can pass
   * parsers and serializers, but the data type must be the same :)
   * */
  namespace?: string;
}

/**
 * A slightly safer version of useQueryState, that requires a QueryValue enum instead of any string
 * The first generic ties the QueryParam to a type to parse the state (e.g. QueryParam.START_DATE returns a Date value)
 * The second generic can be used to narrow down the returned type even further when a param is used widely
 *
 * Ex. QueryParam.VIEW is generically a string but could be narrowed down to different literal union types
 * depending on the context
 *
 **/
export const useTypesafeQueryState = <
  T extends QueryParam,
  V extends QueryState[T] = QueryState[T],
  DV extends V | null = null,
>(
  param: T,
  {
    parse: customParse,
    serialize: customSerialize,
    clearOnDefault = true,
    namespace = "",
    defaultValue,
    disabled,
    history,
  }: UseTypesafeQueryStateOptions<T, V, DV> = {},
): UseTypesafeQueryStateHook<T, V, DV> => {
  const fullParam = namespace ? `${namespace}.${param}` : param;
  const parser = useMemo<Parser<QueryState[T]> & NuqsOptions>(() => {
    let baseParser = getParserForParam(param);
    if (isDefined(customParse)) {
      baseParser.parse = customParse;
    }

    if (isDefined(customSerialize)) {
      baseParser.serialize = customSerialize;
    }

    if (isDefined(defaultValue) && isParserBuilder(baseParser)) {
      baseParser = baseParser.withDefault(defaultValue);
    }

    return {
      history: "push",
      ...baseParser,
      // Last word to the call site: whether a param pushes history is a property of the
      // control writing it (a type-as-you-go input should not fill the back button), not
      // of the param itself, which other pages may drive from a link or a tab.
      ...(isDefined(history) ? { history } : {}),
      clearOnDefault,
      throttleMs: disabled
        ? +Infinity
        : "throttleMs" in baseParser
          ? (baseParser.throttleMs as number)
          : undefined,
    };
  }, [param, customParse, customSerialize, defaultValue, clearOnDefault, disabled, history]);

  const [value, setValue] = useNuqsQueryState<QueryState[T]>(fullParam, parser);

  // nuqs 2.0.0-beta.1 ignores `clearOnDefault` and only drops a param when set to null. Translate a
  // set-to-default into a null set so callers write plain values and the default stays out of the URL.
  const setValueClearingDefault = useCallback<typeof setValue>(
    (update, options) => {
      if (!clearOnDefault || !isDefined(defaultValue)) {
        return setValue(update, options);
      }
      // Casts are needed only because QueryState[T] is an indexed generic; the runtime maps a
      // value equal to the default to null, and passes everything else (including updaters) through.
      const clearIfDefault = (next: unknown) => (next === defaultValue ? null : next);
      const cleared =
        typeof update === "function"
          ? (old: unknown) => clearIfDefault((update as (o: unknown) => unknown)(old))
          : clearIfDefault(update);
      return setValue(cleared as Parameters<typeof setValue>[0], options);
    },
    [setValue, clearOnDefault, defaultValue],
  );

  return [value, setValueClearingDefault] as UseQueryStateReturn<V, DV>;
};

const QUERY_STATES_OPTIONS = {
  history: "push",
} satisfies NuqsOptions;

type ParserMap<T extends QueryParam> = Record<T, Parser<QueryState[T]>>;
type RuntimeParserMap<T extends QueryParam> = Record<string, Parser<QueryState[T]>>;

export type UseTypesafeQueryStatesHook<T extends QueryParam> = UseQueryStatesReturn<ParserMap<T>>;

const getRuntimeQueryParam = (param: string, namespace?: string): string =>
  namespace ? `${namespace}:${param}` : param;

export const useTypesafeQueryStates = <T extends QueryParam>(
  queryParams: readonly T[],
  namespace?: string,
): UseTypesafeQueryStatesHook<T> => {
  const stableQueryParams = useStableArray(queryParams);
  const runtimeParsers = useMemo(() => {
    return Object.fromEntries(
      stableQueryParams.map((param): [string, Parser<QueryState[T]>] => [
        getRuntimeQueryParam(param, namespace),
        getParserForParam(param),
      ]),
    ) as RuntimeParserMap<T>;
  }, [namespace, stableQueryParams]);

  const [runtimeState, setRuntimeState] = useNuqsQueryStates<RuntimeParserMap<T>>(
    runtimeParsers,
    QUERY_STATES_OPTIONS,
  );

  // Keep namespace encoding at the URL boundary. Callers continue to address
  // state by canonical QueryParam keys, while nuqs reads and writes isolated
  // runtime keys such as "online-queries-table-v3:stat".
  const state = useMemo(
    () =>
      Object.fromEntries(
        stableQueryParams.map((param) => [
          param,
          runtimeState[getRuntimeQueryParam(param, namespace)] ?? null,
        ]),
      ) as UseTypesafeQueryStatesHook<T>[0],
    [namespace, runtimeState, stableQueryParams],
  );

  const setState = useCallback<UseTypesafeQueryStatesHook<T>[1]>(
    (updater, options) =>
      setRuntimeState((currentRuntimeState) => {
        const currentState = Object.fromEntries(
          stableQueryParams.map((param) => [
            param,
            currentRuntimeState[getRuntimeQueryParam(param, namespace)] ?? null,
          ]),
        ) as UseTypesafeQueryStatesHook<T>[0];
        const nextState = typeof updater === "function" ? updater(currentState) : updater;
        if (nextState == null) {
          return Object.fromEntries(
            stableQueryParams.map((param) => [getRuntimeQueryParam(param, namespace), null]),
          ) as typeof currentRuntimeState;
        }

        return Object.fromEntries(
          Object.entries(nextState).map(([param, value]) => [
            getRuntimeQueryParam(param, namespace),
            value,
          ]),
        ) as typeof currentRuntimeState;
      }, options),
    [namespace, setRuntimeState, stableQueryParams],
  );

  return [state, setState];
};
